/**
 * WebRTC CDN播放器工具类
 * 用于大班课场景下，使用 TCPlayer 播放 CDN 流
 */

import { uploadLiveLog } from "@saas/components/src/utils/log";

// 错误码映射
const ERROR_MESSAGES = {
  '-1': '播放失败：无效的视频地址',
  '-2': '播放失败：获取视频超时，请检查网络',
  '1': '播放中断：请检查网络连接',
  '2': '加载失败：网络连接异常',
  '3': '播放失败：视频解码错误',
  '4': '播放失败：视频无法加载，请检查网络',
  '5': '播放失败：视频解密错误',
  '10': '请求超时：请检查网络连接',
  '11': '数据异常：无法获取媒体数据',
  '12': '数据异常：媒体数据解析失败',
  '13': '格式错误：当前环境不支持该视频格式',
  '14': '网络错误：请刷新页面重试',
  '15': '媒体错误：请刷新页面重试',
  '16': '播放错误：请刷新页面重试',
  '17': '未知错误：请刷新页面重试',
  '50': '验证失败：License校验失败',
  '51': '验证失败：License已过期',
  '52': '验证失败：域名校验不通过',
  '53': '验证失败：时间验证未通过',
  '54': '验证失败：License类型错误',
  '55': '验证失败：缺少License信息',
  '56': '验证失败：获取License失败',
  '1005': '播放失败：视频流不存在',
  '1013': '鉴权失败：签名缺失',
  '10008': '播放失败：视频资源不存在',
  '-2002': '拉流失败：请稍后重试',
  '-2006': '切换失败：清晰度切换异常'
};

class WebRTCPlayer {
  constructor(options = {}) {
    this.container = options.container;
    this.url = options.url;
    this.onError = options.onError; // 错误回调
    this.onPlaying = options.onPlaying; // 播放回调
    this.player = null;
    this.videoElement = null;
    this.playerId =
      "webrtc-player-" +
      Date.now() +
      "-" +
      Math.random().toString(36).substr(2, 9);
    this.isPlaying = false;
    // 性能追踪
    this.initStartTime = null;
    this.performanceData = {};
  }

  /**
   * 记录关键性能节点并上传日志
   * @param {string} stage 阶段名称
   * @param {object} extraData 额外数据
   */
  logPerformanceStage(stage, extraData = {}) {
    const now = performance.now();
    const elapsed = this.initStartTime
      ? Number((now - this.initStartTime).toFixed(2))
      : 0;
    this.performanceData[stage] = elapsed;

    console.log(`[WebRTCPlayer][性能] ${stage}: +${elapsed}ms`);

    uploadLiveLog({
      message: `CDN播放器-${stage}`,
      level: "info",
      tag: "cdn_player_performance",
      extra: {
        actioin: "startCDNPlayer",
        component: "WebRTCPlayer",
        stage: stage,
        elapsed: elapsed,
        url: this.url,
        allStages: this.performanceData,
        ...extraData,
      },
    });
  }

  /**
   * 初始化播放器
   * @returns {Promise}
   */
  async init() {
    return new Promise((resolve, reject) => {
      try {
        // 关键节点1：初始化开始
        this.initStartTime = performance.now();
        this.logPerformanceStage("1_初始化开始");

        if (!this.container) {
          reject(new Error("容器元素不存在"));
          return;
        }

        // 检查是否已存在 TCPlayer
        if (window.TCPlayer) {
          this.useTCPlayer(resolve, reject);
        } else {
          // 使用原生 video 标签
          this.useNativeVideo(resolve, reject);
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 使用腾讯云 TCPlayer
   */
  useTCPlayer(resolve, reject) {
    try {
      // 清空容器
      this.container.innerHTML = "";

      // 创建 video 元素
      this.videoElement = document.createElement("video");
      this.videoElement.id = this.playerId;
      this.videoElement.style.width = "100%";
      this.videoElement.style.height = "100%";
      this.videoElement.style.objectFit = "contain";
      this.videoElement.setAttribute("playsinline", "true");
      this.videoElement.setAttribute("webkit-playsinline", "true");
      this.videoElement.setAttribute("x5-playsinline", "true");

      // 添加到容器
      this.container.appendChild(this.videoElement);

      // 关键节点2：TCPlayer SDK 初始化
      this.player = window.TCPlayer(this.playerId, {
        sources: [
          {
            src: this.url,
          },
        ],
        autoplay: true,
        preload: "auto",
        playbackRates: [1],
        controls: false,
        width: "100%",
        height: "100%",
        live: true,
        h5_flv: false,
        x5_player: true,
        x5_type: "h5",
        x5_fullscreen: false,
        wording: {
          2032: "请求视频失败，请检查网络",
          2048: "请求m3u8文件失败，可能是网络错误或者跨域问题",
        },
      });
      this.logPerformanceStage("2_SDK初始化完成");
      this.player.on('webrtcevent',function(event) {
        console.log("[WebRTCPlayer] TCPlayer webrtcevent:", event);
        uploadLiveLog({
          message: "CDN播放器-webrtcevent",
          level: "log",
          tag: "cdn_player_webrtcevent",
          extra: {
            actioin:"webrtcevent",
            error: JSON.stringify(event?.data),
            component: "WebRTCPlayer",
            url: this.url
          },
        }); 
      })
      this.player.on('blocked', (event) => {
        console.warn("[WebRTCPlayer] TCPlayer blocked - 尝试自动恢复:", event);
        
        // 尝试静音播放以恢复
        this.onError('播放出错，请刷新重试');

        uploadLiveLog({
          message: "CDN播放器-blocked",
          level: "warn",
          tag: "cdn_player_blocked",
          extra: {
            actioin:"blocked",
            error: JSON.stringify(event?.data),
            component: "WebRTCPlayer",
            url: this.url,
            recovery: "mute_and_play"
          },
        }); 
      })
      // 监听播放器事件
      this.player.on("error", (error) => {
        console.error("[WebRTCPlayer] TCPlayer 播放错误:", error);
        
        // 处理错误提示
        const errorCode = error.code || (error.data && error.data.code);
        // const errorMsg = ERROR_MESSAGES[errorCode] || '播放出错，请刷新重试';
        const errorMsg = '网络不稳定，建议检查网络设置或刷新直播间' 
        if (this.onError && typeof this.onError === 'function') {
          this.onError(errorMsg, error);
        }

        uploadLiveLog({
          message: "CDN播放器-播放错误",
          level: "error",
          tag: "cdn_player_performance",
          extra: {
            actioin:"startCDNPlayer",
            component: "WebRTCPlayer",
            errorCode: errorCode,
            errorMsg: errorMsg,
            error: error?.message || String(error),
            url: this.url,
            allStages: this.performanceData,
          },
        });
      });

      // 关键节点3：开始加载流
      this.player.on("loadstart", () => {
        this.logPerformanceStage("3_开始加载流");
      });

      // 关键节点4：元数据加载完成（CDN响应）
      this.player.on("loadedmetadata", () => {
        this.logPerformanceStage("4_元数据加载完成");
      });

      // 关键节点5：开始播放
      this.player.on("playing", () => {
        this.isPlaying = true;
        if (this.onPlaying && typeof this.onPlaying === 'function') {
          this.onPlaying();
        }
        this.logPerformanceStage("5_开始播放", {
          // 计算各阶段耗时
          sdkInitTime: this.performanceData["2_SDK初始化完成"] || 0,
          loadStartTime:
            (this.performanceData["3_开始加载流"] || 0) -
            (this.performanceData["2_SDK初始化完成"] || 0),
          metadataLoadTime:
            (this.performanceData["4_元数据加载完成"] || 0) -
            (this.performanceData["3_开始加载流"] || 0),
          playStartTime:
            (this.performanceData["5_开始播放"] || 0) -
            (this.performanceData["4_元数据加载完成"] || 0),
        });
      });

      // 监听等待/卡顿事件（可能影响播放体验）
      this.player.on("waiting", () => {
        console.log("[WebRTCPlayer] TCPlayer waiting - 等待数据");
      });

      this.player.on("stalled", () => {
        console.log("[WebRTCPlayer] TCPlayer stalled - 数据停滞");
      });

      resolve();
    } catch (error) {
      console.error("[WebRTCPlayer] TCPlayer 初始化失败:", error);
      reject(error);
    }
  }

  /**
   * 使用原生 video 标签
   */
  useNativeVideo(resolve, reject) {
    try {
      // 清空容器
      this.container.innerHTML = "";

      // 创建 video 元素
      this.videoElement = document.createElement("video");
      this.videoElement.style.width = "100%";
      this.videoElement.style.height = "100%";
      this.videoElement.style.objectFit = "cover";
      this.videoElement.autoplay = true;
      this.videoElement.muted = false;
      this.videoElement.playsInline = true;

      // 设置视频源
      this.videoElement.src = this.url;

      // 添加到容器
      this.container.appendChild(this.videoElement);

      // 监听播放事件
      this.videoElement.addEventListener("playing", () => {
        console.log("[WebRTCPlayer] 原生video开始播放");
        this.isPlaying = true;
        if (this.onPlaying && typeof this.onPlaying === 'function') {
          this.onPlaying();
        }
      });

      this.videoElement.addEventListener("error", (error) => {
        console.error("[WebRTCPlayer] 原生video播放错误:", error);
        
        if (this.onError && typeof this.onError === 'function') {
          this.onError('播放出错，请刷新重试', error);
        }
      });

      // 尝试播放
      this.videoElement
        .play()
        .then(() => {
          resolve();
        })
        .catch((error) => {
          console.error("[WebRTCPlayer] 播放失败:", error);
          reject(error);
        });
    } catch (error) {
      console.error("[WebRTCPlayer] 原生video初始化失败:", error);
      reject(error);
    }
  }

  /**
   * 播放
   * @returns {Promise}
   */
  async play() {
    return new Promise((resolve, reject) => {
      try {
        if (this.player && this.player.play) {
          this.player.play();
          resolve();
        } else if (this.videoElement) {
          this.videoElement
            .play()
            .then(() => {
              this.isPlaying = true;
              resolve();
            })
            .catch(reject);
        } else {
          reject(new Error("播放器未初始化"));
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 暂停
   * @returns {Promise}
   */
  async pause() {
    return new Promise((resolve, reject) => {
      try {
        if (this.player && this.player.pause) {
          this.player.pause();
          this.isPlaying = false;
          resolve();
        } else if (this.videoElement) {
          this.videoElement.pause();
          this.isPlaying = false;
          resolve();
        } else {
          reject(new Error("播放器未初始化"));
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 停止播放
   * @returns {Promise}
   */
  async stop() {
    return new Promise((resolve) => {
      try {
        if (this.player && this.player.pause) {
          this.player.pause();
        } else if (this.videoElement) {
          this.videoElement.pause();
          this.videoElement.src = "";
        }
        this.isPlaying = false;
        resolve();
      } catch (error) {
        console.error("[WebRTCPlayer] 停止播放失败:", error);
        resolve();
      }
    });
  }

  /**
   * 销毁播放器
   */
  destroy() {
    try {
      // 先暂停播放
      this.isPlaying = false;

      if (this.player) {
        // TCPlayer 使用 dispose 方法销毁
        try {
          if (typeof this.player.dispose === "function") {
            this.player.dispose();
          } else if (typeof this.player.destroy === "function") {
            this.player.destroy();
          }
        } catch (e) {
          console.error("[WebRTCPlayer] 销毁player实例失败:", e);
        }
        this.player = null;
      }

      if (this.videoElement) {
        // 停止播放并清除所有资源
        try {
          this.videoElement.pause();
          this.videoElement.removeAttribute("src");
          this.videoElement.load(); // 重新加载以释放资源
          // 移除所有事件监听器
          this.videoElement.onplaying = null;
          this.videoElement.onerror = null;
          // 从DOM中移除
          if (this.videoElement.parentNode) {
            this.videoElement.parentNode.removeChild(this.videoElement);
          }
        } catch (e) {
          console.error("[WebRTCPlayer] 清理video元素失败:", e);
        }
        this.videoElement = null;
      }

      if (this.container) {
        // 清空容器中的所有内容
        try {
          this.container.innerHTML = "";
        } catch (e) {
          console.error("[WebRTCPlayer] 清空容器失败:", e);
        }
      }

      console.log("[WebRTCPlayer] 播放器已销毁");
    } catch (error) {
      console.error("[WebRTCPlayer] 销毁播放器失败:", error);
    }
  }

  /**
   * 设置音量
   * @param {number} volume 0-1之间的值
   */
  setVolume(volume) {
    try {
      if (this.player) {
        // TCPlayer 使用 volume 方法
        if (typeof this.player.volume === "function") {
          this.player.volume(volume);
        }
      } else if (this.videoElement) {
        this.videoElement.volume = volume;
      }
    } catch (error) {
      console.error("[WebRTCPlayer] 设置音量失败:", error);
    }
  }

  /**
   * 静音/取消静音
   * @param {boolean} muted
   */
  setMuted(muted) {
    try {
      if (this.player) {
        // TCPlayer 使用 muted 方法
        if (typeof this.player.muted === "function") {
          this.player.muted(muted);
        }
      } else if (this.videoElement) {
        this.videoElement.muted = muted;
      }
      
      // 触发静音状态回调
      if (this.onMuteChange && typeof this.onMuteChange === 'function') {
        this.onMuteChange(muted);
      }
    } catch (error) {
      console.error("[WebRTCPlayer] 设置静音失败:", error);
    }
  }
}

export default WebRTCPlayer;
